import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import SettingsCostModalsDeleteProfile from 'lumen/components/settings/cost/modals/delete-profile';
import type { CostSettingProfile } from 'lumen/models';

export interface SettingsCostTablesCellsTdWithActionsSignature {
  Args: {
    cellData: CostSettingProfile;
    column: {
      isReadOnly?: boolean;
      onProfileDeleted?: () => void;
    };
  };
  Element: HTMLTableCellElement;
}

const ACTIONS = {
  DELETE: 'delete',
} as const;

type ActionOption = Option & { value: string };

export default class SettingsCostTablesCellsTdWithActions extends Component<SettingsCostTablesCellsTdWithActionsSignature> {
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked showConfirmDeleteModal = false;

  get options(): { icon: 'bin'; label: string; value: string }[] {
    return [
      {
        icon: 'bin',
        label: this.intl.t('general.delete'),
        value: ACTIONS.DELETE,
      },
    ];
  }

  @action handleCloseDeleteModal(): void {
    this.showConfirmDeleteModal = false;
  }

  @action handleOnChange(option: ActionOption): void {
    if (option.value === ACTIONS.DELETE) {
      this.showConfirmDeleteModal = true;
    }
  }

  @action handleProfileDeleted(): void {
    this.args.column.onProfileDeleted?.();
  }

  @action navigateToEdit(): void {
    this.router.transitionTo(
      'market.settings.cost.edit',
      this.args.cellData.id,
    );
  }

  <template>
    {{#unless @column.isReadOnly}}
      <div>
        <PrismButton
          @buttonType="ghost"
          @icon="edit-pencil"
          @onClick={{this.navigateToEdit}}
          @size="small"
          data-test-id="cost-profile-edit"
        />
        <PrismComboSelectInput
          @onChange={{this.handleOnChange}}
          @options={{this.options}}
          @unsafe_triggerComponent={{component
            GeneralDropdownsTriggersTriggerDropdownIconButton
            buttonType="ghost"
            icon="kebab"
            small=true
          }}
          data-test-id="cost-profile-kebab"
        />
      </div>

      {{#if this.showConfirmDeleteModal}}
        <SettingsCostModalsDeleteProfile
          @onClose={{this.handleCloseDeleteModal}}
          @onDeleted={{this.handleProfileDeleted}}
          @profile={{@cellData}}
        />
      {{/if}}
    {{/unless}}
  </template>
}
